<template>
  <div
    data-qa="editableList"
    :class="$style.root"
  >
    <div
      :class="disabled && $style.disabled"
    >
      <ul
        ref="el"
        :class="$style.container"
      >
        <li
          v-for="(item, index) in list"
          :key="item.id"
        >
          <slot
            :item="item"
            name="item"
            :default-item-class="$style.item"
            :handle-input="handleInput"
            :index="index"
          >
            <item
              v-if="list.find(e => e.id === item.id)"
              v-bind="item"
              :id="item.id"
              :index="index"
              :class="$style.item"
              @input="handleInput(item.id, $event, index)"
              @remove="handleRemove"
            />
          </slot>
        </li>
        <li
          :class="$style.footer"
        >
          <slot name="append" />
        </li>
      </ul>
    </div>
    <div :class="$style.controls">
      <slot name="controls" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type Sortable from 'sortablejs';
import fill from 'lodash/fill';
import { useDraggable } from 'vue-draggable-plus';
import { computed, provide, useCssModule, useTemplateRef } from 'vue';
import Item from '@/shared/legacy/ui/editable-list/item.vue';

type Props = {
  disabled?: boolean;
  value: {
    id: number;
    locked?: boolean;
    [key: string]: any;
  }[];
  moveHandler?: (e: Sortable.MoveEvent, originalEvent: Event) => void;
  ghostClass?: string;
  dragHandlerClass?: string;
  dragFilterClass?: string;
};

const {
  disabled,
  value,
  moveHandler,
  ghostClass,
  dragHandlerClass = 'EDITABLE_LIST_DRAG_HANDLER_CLASS',
  dragFilterClass = 'DEFAULT_FILTER_CLASS'
} = defineProps<Props>();

const emit = defineEmits<{
  update: [event: Sortable.SortableEvent];
  input: [list: any[]];
  'drag:start': [event: Sortable.SortableEvent];
  'drag:end': [event: Sortable.SortableEvent];
}>();

provide('$draggableDragHandlerClass', dragHandlerClass);
provide('$draggableFilterClass', dragFilterClass);

const onUpdate = (event: Sortable.SortableEvent) => {
  emit('update', event);
};
const onStart = (event: Sortable.SortableEvent) => {
  emit('drag:start', event);
};
const onEnd = (event: Sortable.SortableEvent) => {
  emit('drag:end', event);
};

const handleInput = (id: number, data: any, index: number) => {
  if (disabled) {
    return;
  }
  const oldItem = value[index];
  const newItem = {
    ...oldItem,
    data
  };
  const newValue = fill([...value], newItem, index, index + 1);
  emit('input', newValue);
};
const handleRemove = (id: number) => {
  if (disabled) {
    return;
  }
  emit(
    'input',
    value.filter((item) => item.id !== id)
  );
};
const onMove = (e: Sortable.MoveEvent, originalEvent: Event) => {
  if (disabled) {
    return;
  }
  if (e.related.children[0].dataset.orderable !== 'true') {
    return false;
  }
  if (moveHandler) {
    return moveHandler(e, originalEvent);
  }
};

const el = useTemplateRef<HTMLElement>('el');
const style = useCssModule();

const list = computed({
  get: () => value,
  set: (newValue) => {
    emit('input', newValue);
  }
});

useDraggable(el, list, {
  animation: 150,
  ghostClass: ghostClass || style.ghost,
  handle: `.${dragHandlerClass}`,
  filter: `.${dragFilterClass}`,
  preventOnFilter: false,
  onUpdate,
  onStart,
  onMove,
  onEnd
});
</script>

<style module>
.root {
  --draghandler-width: 15px;
  --draghandler-margin: 10px;
  --lockicon-width: 30px;
  --removebutton-width: 30px;
  --item-margin: 10px;
}

.footer, .controls {
  padding-right: var(--removebutton-width);
}

.container {
  list-style: none;
  padding: 0;
  margin: 0;
  > * + * {
    margin-top: var(--item-margin);
  }
}

.ghost {
  opacity: 0;
}

.disabled {
  position: relative;
  input[disabled] {
    color: initial;
  }
}

.item {
  margin-bottom: 10px;
}
</style>

<i18n lang="json">{}</i18n>
